import React, {
  createContext,
  type ReactNode,
  useState,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import { GroupType } from '../validation/group/groupType';
import { getGroupsByUser } from '../api/firebase/firestore/group';
import { AuthContext } from './auth';

type GroupContextType = {
  groups: GroupType[];
  isLoadingGroups: boolean;
  refreshGroups: () => Promise<void>;
};

export const GroupContext = createContext<GroupContextType>({
  groups: [],
  isLoadingGroups: true,
  refreshGroups: async () => {
    /* noop */
  },
});

export const GroupProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { user } = useContext(AuthContext);
  const [groups, setGroups] = useState<GroupType[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(true);

  const refreshGroups = useCallback(async () => {
    if (!user?.uid) {
      setGroups([]);
      setIsLoadingGroups(false);
      return;
    }
    setIsLoadingGroups(true);
    try {
      const userGroups = await getGroupsByUser(user.uid);
      setGroups(userGroups);
    } catch {
      setGroups([]);
    } finally {
      setIsLoadingGroups(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    refreshGroups();
  }, [refreshGroups]);

  return (
    <GroupContext.Provider value={{ groups, isLoadingGroups, refreshGroups }}>
      {children}
    </GroupContext.Provider>
  );
};
